/**
 * Where to send a citizen after a detour.
 *
 * Emails now carry a button straight to one application. A citizen who is
 * signed out, or who has never filled in their claimant information, is pushed
 * aside first and has to be put back afterwards. The intended destination
 * travels as a `redirect` query while that happens.
 *
 * Two things this has to get right, and both have bitten us:
 *
 * 1. It must refuse anything that is not an internal path. A value that starts
 *    "//host" or "/\host" is read by browsers as protocol-relative and would
 *    turn our own login page into an open redirect, which is a phishing
 *    primitive an attacker can put in front of a real NIB sign-in form.
 *
 * 2. The caller must push the RESULT AS A STRING. Vue Router 4 keeps only the
 *    path when it is handed an object whose `path` contains a query string, so
 *    `router.push({ path: "/claims/9?type=Funeral" })` silently arrives with no
 *    type. The claim page needs that query for its title AND the reupload modal
 *    needs it to pick a strategy, so dropping it breaks the exact journey the
 *    email button exists to create. `router.push("/claims/9?type=Funeral")`
 *    parses the whole thing.
 */

/** The internal path to resume, or null when there is nothing safe to resume. */
export function safeRedirect(raw: unknown): string | null {
  if (typeof raw !== "string") return null;

  const value = raw.trim();
  if (!value.startsWith("/")) return null;

  // "//host" and "/\host" are both protocol-relative to a browser.
  const second = value.charAt(1);
  if (second === "/" || second === "\\") return null;

  return value;
}

/** The same answer, with the portal home as the fallback. */
export function redirectOrHome(raw: unknown): string {
  return safeRedirect(raw) ?? "/";
}
